import React, { useState } from 'react';
import {
  Box, Typography, Button, Card, TextField, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';

const EMPTY_PROMPT = { title: '', description: '', content: '', category: '', agent_id: null };

export default function PromptManager({ prompts, onRefresh, userEmail, agents = [] }) {
  const [formOpen, setFormOpen] = useState(false);
  const [editPrompt, setEditPrompt] = useState(null);
  const [form, setForm] = useState(EMPTY_PROMPT);

  const openCreate = () => {
    setEditPrompt(null);
    setForm(EMPTY_PROMPT);
    setFormOpen(true);
  };

  const openEdit = (prompt) => {
    setEditPrompt(prompt);
    setForm({
      title: prompt.title,
      description: prompt.description || '',
      content: prompt.content,
      category: prompt.category || '',
      agent_id: prompt.agent_id || null,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Titel en content zijn verplicht');
      return;
    }
    try {
      const url = editPrompt
        ? `${AI_ENGINE_URL}/api/v1/chat/prompts/${editPrompt.id}`
        : `${AI_ENGINE_URL}/api/v1/chat/prompts`;
      const method = editPrompt ? 'PUT' : 'POST';
      const body = editPrompt ? form : { ...form, created_by: userEmail };

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Opslaan mislukt');
      toast.success(editPrompt ? 'Prompt bijgewerkt' : 'Prompt aangemaakt');
      setFormOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (prompt) => {
    if (!(await bevestig({ titel: 'Prompt verwijderen?', tekst: `Prompt "${prompt.title}" wordt definitief verwijderd.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/prompts/${prompt.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      toast.success('Prompt verwijderd');
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Prompts</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontSize: 13 }}>
          Nieuw
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {prompts.map((prompt) => (
          <Card key={prompt.id} variant="outlined" sx={{ p: 1.5, borderColor: 'rgba(45,51,53,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography fontSize={13} fontWeight={600} noWrap>{prompt.title}</Typography>
                  {prompt.category && (
                    <Chip label={prompt.category} size="small" sx={{ fontSize: 10, height: 18 }} />
                  )}
                </Box>
                <Typography fontSize={11} color="text.secondary" noWrap>{prompt.description}</Typography>
                {prompt.agent_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <SmartToyIcon sx={{ fontSize: 12, color: '#526075' }} />
                    <Typography fontSize={10} color="primary">{prompt.agent_name}</Typography>
                  </Box>
                )}
              </Box>
              <IconButton size="small" onClick={() => openEdit(prompt)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
              <IconButton size="small" onClick={() => handleDelete(prompt)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
            </Box>
          </Card>
        ))}
        {prompts.length === 0 && (
          <Typography sx={{ py: 3, textAlign: 'center', color: '#adb3b5', fontSize: 13 }}>
            Nog geen prompt templates. Maak er een aan.
          </Typography>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editPrompt ? 'Prompt bewerken' : 'Nieuwe Prompt'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Titel"
            size="small"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            fullWidth
          />
          <TextField
            label="Beschrijving"
            size="small"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
          />
          <TextField
            label="Categorie"
            size="small"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            fullWidth
            placeholder="bijv. Marketing, Development, Writing"
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Gekoppelde Agent</InputLabel>
            <Select
              value={form.agent_id || ''}
              onChange={(e) => setForm({ ...form, agent_id: e.target.value || null })}
              label="Gekoppelde Agent"
            >
              <MenuItem value="">Geen agent</MenuItem>
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Prompt Content"
            multiline
            minRows={4}
            maxRows={10}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            fullWidth
            placeholder="Schrijf hier je prompt template..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Annuleren</Button>
          <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#526075', '&:hover': { bgcolor: '#465469' } }}>
            {editPrompt ? 'Opslaan' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
